import { Col, Form, Row } from "antd";
import { useEffect, useState } from "react";
import {
  addLabel,
  updateLabel,
  deleteLabel,
} from "../../repository/data.repository";
import { LabelsForm } from "./LabelsFormDialog.default";
import DynamicForm from "../../controls/DynamicForm";
import InModal from "../../controls/InModal";
import { modalTitle } from "../../controls/InModal.default";
import ModalFooter from "../../controls/ModalFooter";
import ModalDeleteConfirmation from "../../controls/ModalDeleteConfirmation";
import ModalErrorAlert from "../../controls/ModalErrorAlert";
import LabelRolesManagement from "./LabelRolesManagement";
import LabelUsersManagement from "./LabelUsersManagement";
import { hasFormTouched } from "../../utils/hasFormChanged";
import { setFormFields } from "../../utils/setFormFields";
import { getProblemMessage } from "../../utils/http";

export interface LabelsFormDialogProps {
  readonly open?: boolean;
  readonly mode: "add" | "update" | "delete";
  readonly initialValues?: LabelsForm;
  readonly onFinish?: () => void;
  readonly onCancel?: () => void;
}

function LabelsFormDialog(props: LabelsFormDialogProps) {
  const [form] = Form.useForm();
  const values = Form.useWatch<LabelsForm>([], form);
  const [valid, setValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  //Lifecycle

  useEffect(() => {
    if (props.open) {
      if (props.initialValues) {
        setFormFields(form, props.initialValues);
      }
    } else {
      reset();
    }
  }, [props.open]);

  useEffect(() => {
    form
      .validateFields({
        validateOnly: true,
      })
      .then((value) => {
        setValid(!!Object.values(value).length);
      })
      .catch(() => {
        setValid(false);
      });
  }, [form, values, props.open]);

  //Events

  function onSubmit(): void {
    const { name, description } = values;
    setLoading(true);
    setErrorMessage(undefined);
    if (props.mode === "add") {
      addLabel({
        name,
        description,
      })
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch((err) => {
          setLoading(false);
          setErrorMessage(getProblemMessage(err));
        });
    } else if (props.mode === "update") {
      if (hasFormTouched(form)) {
        updateLabel({
          uid: props.initialValues?.uid,
          rowVersion: props.initialValues?.rowVersion,
          name,
          description,
        })
          .then(() => {
            setLoading(false);
            props.onFinish?.();
          })
          .catch((err) => {
            setLoading(false);
            setErrorMessage(getProblemMessage(err));
          });
      } else {
        setLoading(false);
        props.onFinish?.();
      }
    } else if (props.mode === "delete") {
      deleteLabel(props.initialValues?.uid!)
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch((err) => {
          setLoading(false);
          setErrorMessage(getProblemMessage(err));
        });
    }
  }

  function onCancel(): void {
    props.onCancel?.();
  }

  // Methods

  function reset(): void {
    setLoading(false);
    setValid(false);
    setErrorMessage(undefined);
    form.resetFields();
  }

  const labelForm = (
    <DynamicForm
      className="w-auto"
      form={form}
      schema={{
        groups: [
          {
            controls: [
              {
                type: "textbox",
                name: "name",
                label: "Name",
                rules: [
                  {
                    required: props.mode !== "update",
                    message: "Name is required",
                  },
                ],
              },
              {
                type: "areabox",
                name: "description",
                label: "Description",
                rows: 5,
              },
            ],
          },
        ],
      }}
    />
  );

  return (
    <InModal
      key={props.mode}
      open={props.open}
      size={
        props.mode === "delete"
          ? "small"
          : props.mode === "update"
            ? "xxl"
            : "medium"
      }
      title={modalTitle(props.mode, "Label", props.initialValues?.name)}
      onCancel={onCancel}
      actions={
        <ModalFooter
          mode={props.mode}
          loading={loading}
          valid={valid}
          onCancel={onCancel}
          onSubmit={onSubmit}
        />
      }
    >
      {props.mode === "add" && labelForm}

      {/* Update: edit the label's fields on the left; manage its role targeting (applied immediately) on the right. */}
      {props.mode === "update" && (
        <Row className="flex flex-row flex-nowrap gap-4">
          <Col className="flex min-w-0 flex-1 flex-col">{labelForm}</Col>
          <Col className="flex min-w-0 flex-1 flex-col">
            {props.initialValues?.uid && (
              <LabelRolesManagement
                labelUid={props.initialValues.uid}
                setError={setErrorMessage}
              />
            )}
          </Col>
          <Col className="flex min-w-0 flex-1 flex-col">
            {props.initialValues?.uid && (
              <LabelUsersManagement
                labelUid={props.initialValues.uid}
                setError={setErrorMessage}
              />
            )}
          </Col>
        </Row>
      )}

      {props.mode === "delete" && (
        <ModalDeleteConfirmation
          entityLabel="label"
          entityName={props.initialValues?.name}
        />
      )}
      <ModalErrorAlert message={errorMessage} />
    </InModal>
  );
}

export default LabelsFormDialog;
